import { Controller } from "@hotwired/stimulus"

// New-meeting form: when the user changes the Time zone and the Start
// time hasn't been touched yet, reset Start time to the CURRENT time in
// the newly-selected zone (rounded up to the next 15 min — the same rule
// the server uses for the initial default).
//
// "Touched" is detected via the start time-select's `time-select:change`
// event, which fires ONLY on real user action (its programmatic
// `setValue` is silent), so our own updates never count as a touch.
const DAY_MIN = 24 * 60

export default class extends Controller {
  static targets = ["timezone", "start"]
  static values = { tzMap: Object }

  connect() {
    this.touched = false
    this._programmatic = false
    this._onStartChange = () => {
      if (!this._programmatic) this.touched = true
    }
    if (this.hasStartTarget) {
      this.startTarget.addEventListener("time-select:change", this._onStartChange)
    }
  }

  disconnect() {
    if (this.hasStartTarget && this._onStartChange) {
      this.startTarget.removeEventListener("time-select:change", this._onStartChange)
    }
  }

  // Fired on the Time zone <select> change.
  timezoneChanged() {
    if (this.touched) return
    if (!this.hasTimezoneTarget || !this.hasStartTarget) return

    const hhmm = this._roundedNowIn(this._ianaFor(this.timezoneTarget.value))
    if (!hhmm) return

    const ts = this.application.getControllerForElementAndIdentifier(
      this.startTarget,
      "time-select"
    )
    if (!ts) return

    ts.setValue(hhmm)
    // Re-emit so Duration / End stay in sync — flagged so our own
    // touched-listener ignores this programmatic change.
    this._programmatic = true
    this.startTarget.dispatchEvent(
      new CustomEvent("time-select:change", { bubbles: true })
    )
    this._programmatic = false
  }

  // "Start now" button — set the Date to today and the Start time to the
  // EXACT current time in the selected zone, so the meeting goes live the
  // moment it's created. End slides via meeting-duration (it listens on the
  // start time-select's change).
  startNow() {
    const z = this.nowInSelectedZone()
    if (!z) return

    const dateInput = this.element.querySelector(
      'input[data-meeting-recurrence-target="dateInput"]'
    )
    if (dateInput && dateInput._flatpickr) {
      dateInput._flatpickr.setDate(z.date, true)
      dateInput.dispatchEvent(new Event("change", { bubbles: true }))
    }

    if (!this.hasStartTarget) return
    const ts = this.application.getControllerForElementAndIdentifier(
      this.startTarget, "time-select"
    )
    ts?.setValue(z.time)
    // Re-emit so Duration / End recalculate (NOT flagged programmatic — the
    // user explicitly set "now", so it counts as a real touch).
    this.startTarget.dispatchEvent(
      new CustomEvent("time-select:change", { bubbles: true })
    )
  }

  // ─── private ──────────────────────────────────────────────────

  // ActiveSupport zone label (the <select> value) → IANA id the browser
  // understands. Falls back to the raw value if it's missing from the map.
  _ianaFor(tzName) {
    const map = this.tzMapValue || {}
    return map[tzName] || tzName
  }

  // "Now" in the currently-selected zone as { date: "YYYY-MM-DD",
  // time: "HH:MM" } — EXACT (no rounding), so the "Start now" button can
  // make a meeting start the moment it's created. null if unusable.
  nowInSelectedZone() {
    if (!this.hasTimezoneTarget) return null
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: this._ianaFor(this.timezoneTarget.value),
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: false
      }).formatToParts(new Date())
      const get = (t) => parts.find((p) => p.type === t)?.value
      let hh = get("hour"); if (hh === "24") hh = "00"
      const date = `${get("year")}-${get("month")}-${get("day")}`
      const time = `${hh}:${get("minute")}`
      if (date.includes("undefined") || time.includes("undefined")) return null
      return { date, time }
    } catch (e) {
      return null
    }
  }

  // Current wall-clock time in `iana`, rounded UP to the next 15-min mark.
  // Returns "HH:MM" (24h) or null when the zone is unusable.
  _roundedNowIn(iana) {
    let h, m
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: iana,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      }).formatToParts(new Date())
      h = parseInt(parts.find((p) => p.type === "hour").value, 10)
      m = parseInt(parts.find((p) => p.type === "minute").value, 10)
    } catch (e) {
      return null
    }
    if (Number.isNaN(h) || Number.isNaN(m)) return null
    if (h === 24) h = 0 // some engines render midnight as "24" with hour12:false

    const total = h * 60 + m
    // +1 so an exact 15-mark bumps to the next one (mirrors the server).
    let rounded = Math.ceil((total + 1) / 15) * 15
    rounded = ((rounded % DAY_MIN) + DAY_MIN) % DAY_MIN
    const hh = String(Math.floor(rounded / 60)).padStart(2, "0")
    const mm = String(rounded % 60).padStart(2, "0")
    return `${hh}:${mm}`
  }
}
